import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import { StatTile, Sparkline, RouteBreakdown, CostByModel } from '../components/DashboardBits';
import { TrendingDown } from 'lucide-react';

export default function AdminDashboardPage() {
  const { lang } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/dashboard/admin`).then(r => setD(r.data));
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  if (!d) return <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="skeleton h-24 rounded-sm" />)}</div>;

  return (
    <div className="fade-up" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="data-label accent-line text-[#DDA079]">Real-time · refreshes every 6s</div>
          <h1 className="font-headings text-4xl text-white mt-2">Cost Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-400 rounded-full pulse-dot" />
          <span className="data-label text-emerald-400">LIVE</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatTile testid="stat-queries-today" label={t(lang, 'common.today')} value={d.today_queries} sub="queries" />
        <StatTile testid="stat-free-pct" label="Free %" value={`${d.today_free_pct}%`} sub="today" accent="text-emerald-400" />
        <StatTile testid="stat-spend-today" label="AI spend today" value={`₹${d.today_spend_inr.toFixed(2)}`} accent="text-[#DDA079]" />
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="v-card p-5 col-span-2">
          <div className="flex justify-between items-start mb-1">
            <div className="data-label">Spend over last 14 days · 30d total</div>
            <div className="font-mono text-[11px] text-slate-500">₹{d.total_spend_inr.toFixed(2)}</div>
          </div>
          <Sparkline data={d.sparkline} />
          <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-white/5">
            <div>
              <div className="data-label">30d queries</div>
              <div className="font-mono text-2xl text-white mt-1">{d.total_queries.toLocaleString('en-IN')}</div>
            </div>
            <div>
              <div className="data-label">30d free %</div>
              <div className="font-mono text-2xl text-emerald-400 mt-1">{d.free_pct}%</div>
            </div>
          </div>
        </div>
        <div className="v-card p-5">
          <div className="data-label mb-3">Query route distribution</div>
          <RouteBreakdown byRoute={d.by_route} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="v-card p-5">
          <div className="data-label mb-3">Cost by model (30d)</div>
          <CostByModel costByModel={d.cost_by_model_inr} />
        </div>
        <div className="v-card p-5">
          <div className="data-label mb-3">Per-department breakdown</div>
          <div className="space-y-2">
            {Object.entries(d.per_department).map(([dept, v]) => (
              <div key={dept} className="border border-white/5 p-3" data-testid={`dept-row-${dept}`}>
                <div className="flex justify-between mb-2">
                  <span className="text-white text-[14px]">{dept}</span>
                  <span className="font-mono text-[11px] text-slate-400">{v.queries} queries</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <div className="data-label">Free</div>
                    <div className="font-mono text-emerald-400">{v.free_pct}%</div>
                  </div>
                  <div>
                    <div className="data-label">Spend</div>
                    <div className="font-mono text-white">₹{v.spend_inr.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="data-label">Avg/query</div>
                    <div className="font-mono text-slate-300">₹{v.queries ? (v.spend_inr / v.queries).toFixed(3) : '0.00'}</div>
                  </div>
                </div>
              </div>
            ))}
            {Object.keys(d.per_department).length === 0 && (
              <div className="text-slate-500 text-[13px]">{t(lang, 'common.empty')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
